import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldArticle, ldFAQ, ldBreadcrumb, ldPerson } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { Clock, ArrowLeft, ArrowRight, ShieldCheck, MapPin, Star } from "lucide-react";

const PROFESSION_LABELS = {
  "avocat": "Avocat",
  "expert-comptable": "Expert-comptable",
  "notaire": "Notaire",
};

// Lightweight markdown-ish renderer for our seed articles (## H2, ** bold)
function renderBody(body) {
  if (!body) return null;
  const blocks = body.split(/\n\n+/);
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-serif text-2xl md:text-3xl tracking-tight text-[#0B3A2A] mt-12 mb-4">
          {block.slice(3)}
        </h2>
      );
    }
    if (block.startsWith("### ")) {
      return (
        <h3 key={i} className="font-serif text-xl text-[#0B3A2A] mt-8 mb-3">
          {block.slice(4)}
        </h3>
      );
    }
    // Bold inline rendering with **
    const parts = block.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="text-[15.5px] text-slate-700 leading-[1.85] mb-5">
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**")
            ? <strong key={j} className="font-semibold text-[#0B3A2A]">{p.slice(2, -2)}</strong>
            : <span key={j}>{p}</span>
        )}
      </p>
    );
  });
}

export default function InsightArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setData(null);
    apiClient.get(`/insights/${slug}`)
      .then(r => setData(r.data))
      .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#FAFAF7] min-h-screen">
        <Navbar variant="light" />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-500">Chargement…</div>
        <Footer />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="bg-[#FAFAF7] min-h-screen">
        <Seo title="Article introuvable" noindex />
        <Navbar variant="light" />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="font-serif text-3xl text-[#0B3A2A] mb-4">Article introuvable</h1>
          <p className="text-slate-600 mb-6">Cet article n'est pas ou plus disponible.</p>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/insights"><ArrowLeft className="w-4 h-4 mr-2" /> Tous les articles</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const a = data.article;
  const related = data.related || [];
  const authorExpert = data.author_expert;
  const jsonLd = [
    ldArticle(a),
    ldBreadcrumb([
      { name: "Accueil", url: "/" },
      { name: "Insights", url: "/insights" },
      { name: a.title, url: `/insights/${a.slug}` },
    ]),
  ];
  if (a.faq?.length) jsonLd.push(ldFAQ(a.faq));
  if (authorExpert) jsonLd.push(ldPerson(authorExpert));

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title={a.seo_meta_title || a.title}
        description={a.seo_meta_description || a.excerpt}
        image={a.seo_og_image || a.cover_image}
        keywords={a.seo_keywords}
        type="article"
        canonical={typeof window !== "undefined" ? `${window.location.origin}/insights/${a.slug}` : undefined}
        jsonLd={jsonLd}
      />
      <Navbar variant="light" />

      {/* Cover */}
      {a.cover_image && (
        <div className="w-full bg-[#0B3A2A]">
          <div className="max-w-5xl mx-auto">
            <div className="aspect-[21/9] overflow-hidden">
              <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover opacity-90" />
            </div>
          </div>
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 mb-5" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-[#0B3A2A]">Accueil</Link>
          <span className="mx-2">/</span>
          <Link to="/insights" className="hover:text-[#0B3A2A]">Insights</Link>
        </nav>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          {(a.profession_tags || []).map(t => (
            <Link key={t} to={`/specialistes/${t}`} className="text-[10px] uppercase tracking-wider text-[#8A6E3A] bg-[#C9A961]/10 border border-[#C9A961]/20 rounded-full px-2.5 py-1 hover:bg-[#C9A961]/20 transition-smooth">
              {PROFESSION_LABELS[t] || t}
            </Link>
          ))}
        </div>

        <h1 className="font-serif text-[32px] md:text-[44px] lg:text-[52px] tracking-tight text-[#0B3A2A] leading-[1.1] mb-6" data-testid="article-title">
          {a.title}
        </h1>
        {a.excerpt && (
          <p className="text-lg text-slate-600 leading-relaxed mb-8 italic">{a.excerpt}</p>
        )}

        <div className="flex items-center justify-between flex-wrap gap-4 pb-8 mb-10 border-b border-slate-100 text-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B3A2A] flex items-center justify-center text-white font-serif text-sm">
              {(a.author_name || "C")[0]}
            </div>
            <div>
              <div className="font-medium text-[#0B3A2A]">{a.author_name}</div>
              {a.author_profession && <div className="text-xs text-slate-500">{a.author_profession}</div>}
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            {a.published_at && (
              <time dateTime={a.published_at}>
                {new Date(a.published_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
              </time>
            )}
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {a.reading_time || 1} min
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="prose prose-slate max-w-none" data-testid="article-body">
          {renderBody(a.body)}
        </div>

        {/* FAQ */}
        {a.faq?.length > 0 && (
          <section className="mt-16 pt-10 border-t border-slate-100" data-testid="article-faq">
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#0B3A2A] mb-8">
              Questions fréquentes
            </h2>
            <div className="space-y-5">
              {a.faq.map((it, i) => (
                <div key={i} className="bg-[#FAFAF7] border border-slate-100 rounded-xl p-5">
                  <div className="font-serif text-lg text-[#0B3A2A] mb-2">{it.q}</div>
                  <div className="text-sm text-slate-700 leading-relaxed">{it.a}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Author spécialiste card */}
        {authorExpert && (
          <section className="mt-16 pt-10 border-t border-slate-100">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-4">Auteur</div>
            <Link to={`/experts/${authorExpert.id}`} className="block bg-white rounded-2xl border border-slate-100 p-6 hover:border-[#C9A961]/40 hover:shadow-md transition-smooth">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                  {authorExpert.photo && <img src={authorExpert.photo} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-serif text-xl text-[#0B3A2A]">{authorExpert.first_name} {authorExpert.last_name}</h3>
                    {authorExpert.verified && <ShieldCheck className="w-4 h-4 text-[#C9A961]" />}
                  </div>
                  <p className="text-sm text-slate-600">{authorExpert.profession}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                    {authorExpert.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{authorExpert.city}</span>}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
            </Link>
          </section>
        )}

        {/* CTA */}
        <section className="mt-16 bg-gradient-to-br from-[#0B3A2A] to-[#26334F] text-white rounded-2xl p-8 lg:p-10 text-center">
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-3">Une situation similaire à analyser ?</h3>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            Identifions ensemble le spécialiste le plus adapté à votre dossier.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold">
              <Link to="/orientation">Démarrer l'orientation</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">
              <Link to="/missions/new">Soumettre une demande</Link>
            </Button>
          </div>
        </section>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#FAFAF7] border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-4">À lire également</div>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#0B3A2A] mb-8">Analyses associées</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map(r => (
                <Link key={r.id} to={`/insights/${r.slug}`} data-testid={`related-${r.slug}`} className="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:border-[#C9A961]/40 transition-smooth">
                  {r.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={r.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-smooth" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-serif text-lg text-[#0B3A2A] leading-snug mb-2 line-clamp-2">{r.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{r.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
